import { useState, type FormEvent } from 'react'
import { useQueryClient, useMutation, useQuery } from 'react-query'
import { toast } from 'react-toastify'

import {
  TOAST_ERROR_DEFAULT_OPTIONS,
  TOAST_SUCCESS_DEFAULT_OPTIONS,
} from '../../../../../../constants/toast'
import { api } from '../../../../../../services/api'
import { getErrorMessage } from '../../../../../../utils/getErrorMessage'
import { type TaskCommentDomain } from '../../../../../../types/domain/task-comment-domain'
import { type TextareaSuggestableActions } from '../TextareaSuggestable'
import type UserDomain from '../../../../../../types/domain/user-domain'

type MutationArgs = {
  taskId: number
  content: string
  contentRaw: string
  commentId?: number
  mentions: Array<{ id: number, email: string, name: string }>
}

export const useComments = (
  taskId: number,
  user: UserDomain,
  textareaSuggestableMethodsRef: React.MutableRefObject<TextareaSuggestableActions | null>
) => {
  const [commentToDelete, setCommentToDelete] =
    useState<TaskCommentDomain | null>(null)
  const [isDeleteCommentModalOpen, setIsDeleteCommentModalOpen] =
    useState(false)
  const [selectedComment, setSelectedComment] =
    useState<TaskCommentDomain | null>(null)

  const queryClient = useQueryClient()

  const addCommentMutation = useMutation({
    async mutationFn ({ taskId, content, mentions, contentRaw }: MutationArgs) {
      const response = await api.post('/task-comments', {
        taskId,
        content,
        mentions,
        contentRaw,
      })
      return response.data
    },
  })

  const updateCommentMutation = useMutation({
    async mutationFn ({ taskId, commentId, content, contentRaw }: MutationArgs) {
      const response = await api.put(`/task-comments/${commentId}`, {
        taskId,
        content,
        contentRaw,
      })
      return response.data
    },
  })

  const deleteCommentMutation = useMutation({
    async mutationFn (id: number) {
      const response = await api.delete(`/task-comments/${id}`)
      return response.data
    },
  })

  const { data: comments = [] } = useQuery<TaskCommentDomain[]>(
    ['comments', taskId],
    async () => {
      const response = await api.get(`/task-comments/${taskId}`)
      return response.data
    }
  )

  const handleAddComment = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const textareaSuggestableMethods = textareaSuggestableMethodsRef?.current

    if (!textareaSuggestableMethods) {
      return
    }

    const content = textareaSuggestableMethods.getPlainText()
    const contentRaw = textareaSuggestableMethods.getRawContent()
    const mentions = textareaSuggestableMethods.getExtractedMentions()

    const filteredMentions = mentions.filter((mention, index, array) => {
      const mentionedUserIndex = array.findIndex((mentionedUser) => {
        return mentionedUser.id === mention.id
      })

      return mentionedUserIndex === index
    })

    const payload = {
      contentRaw: JSON.stringify(contentRaw),
      content,
      taskId,
      mentions: filteredMentions,
    }

    if (selectedComment) {
      updateCommentMutation.mutate(
        {
          ...payload,
          commentId: selectedComment.id,
        },
        {
          onSuccess () {
            setSelectedComment(null)
            textareaSuggestableMethods.clear()
            queryClient.invalidateQueries(['comments', taskId])
          },
          onError (error) {
            toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
          },
        }
      )

      return
    }

    addCommentMutation.mutate(payload, {
      onSuccess () {
        textareaSuggestableMethods.clear()
        queryClient.invalidateQueries(['comments', taskId])

        const authUserEmail = user.email
        const foundUserMention = filteredMentions.find(
          ({ email }) => email === authUserEmail
        )

        if (foundUserMention) {
          queryClient.invalidateQueries('notifications')
        }
      },
      onError (error) {
        toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
      },
    })
  }

  const handleDeleteComment = () => {
    deleteCommentMutation.mutate(commentToDelete!.id, {
      onSuccess () {
        queryClient.invalidateQueries(['comments', taskId])

        setCommentToDelete(null)
        setIsDeleteCommentModalOpen(false)
        toast.success(
          'Comentário excluído com sucesso!',
          TOAST_SUCCESS_DEFAULT_OPTIONS
        )
      },
      onError (error) {
        toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
      },
    })
  }

  const handleSelectCommentToEdit = (comment: TaskCommentDomain) => {
    const isCommentCurrentlySelected =
      selectedComment && selectedComment.id === comment.id

    if (isCommentCurrentlySelected) {
      setSelectedComment(null)
      return
    }

    setSelectedComment(comment)
  }

  const handleCancelCommentEdition = () => {
    textareaSuggestableMethodsRef?.current?.clear()
    setSelectedComment(null)
  }

  const handleCloseDeleteCommentModal = () => {
    setCommentToDelete(null)
    setIsDeleteCommentModalOpen(false)
  }

  const handleDeleteButtonClick = (comment: TaskCommentDomain) => {
    textareaSuggestableMethodsRef.current?.clear()
    setSelectedComment(null)
    setCommentToDelete(comment)
    setIsDeleteCommentModalOpen(true)
  }

  return {
    comments,
    isDeleteCommentModalOpen,
    selectedComment,
    addCommentMutation,
    deleteCommentMutation,
    updateCommentMutation,
    commentToDelete,
    handleAddComment,
    handleDeleteComment,
    handleSelectCommentToEdit,
    handleCancelCommentEdition,
    handleCloseDeleteCommentModal,
    handleDeleteButtonClick,
  }
}
